import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Save, Plus, Trash2, LogOut, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-stone-900/15 bg-white px-3 py-2.5 text-sm font-light outline-none transition-colors focus:border-gold";

const Field = ({ label, textarea, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500">
      {label}
    </span>
    {textarea ? (
      <textarea rows={textarea} {...props} className={`${inputCls} resize-y`} />
    ) : (
      <input {...props} className={inputCls} />
    )}
  </label>
);

const errText = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return e.message || "Something went wrong";
};

const EMPTY_VILLA = {
  title: "",
  location: "Canggu",
  guests: 4,
  bedrooms: 2,
  bathrooms: 2,
  rating: 5.0,
  reviews_count: 0,
  airbnb_url: "",
  airbnb_active: true,
  whatsapp_message: "",
  description: "",
  images: [],
};

const EMPTY_REVIEW = { quote: "", author: "", country: "", villa: "", rating: 5 };

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("bbv_admin_token") || "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("villas");
  const [config, setConfig] = useState(null);
  const [villas, setVillas] = useState([]);
  const [reviews, setReviews] = useState([]);

  const auth = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const load = useCallback(async () => {
    const [c, v, r] = await Promise.all([
      axios.get(`${API}/config`),
      axios.get(`${API}/villas`),
      axios.get(`${API}/reviews`),
    ]);
    setConfig(c.data);
    setVillas(v.data);
    setReviews(r.data);
  }, []);

  useEffect(() => {
    if (token) load().catch(() => setToken(""));
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("bbv_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(errText(err));
    }
  };

  const logout = () => {
    localStorage.removeItem("bbv_admin_token");
    setToken("");
  };

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2500);
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/admin/config`, config, auth());
      flash("Settings saved");
    } catch (err) {
      setError(errText(err));
    }
  };

  const saveVilla = async (villa) => {
    try {
      const payload = { ...villa, images: villa.images.filter(Boolean) };
      if (villa._isNew) {
        delete payload._isNew;
        await axios.post(`${API}/admin/villas`, payload, auth());
      } else {
        await axios.put(`${API}/admin/villas/${villa.slug}`, payload, auth());
      }
      await load();
      flash("Villa saved");
    } catch (err) {
      setError(errText(err));
    }
  };

  const deleteVilla = async (slug) => {
    if (!window.confirm("Delete this villa?")) return;
    await axios.delete(`${API}/admin/villas/${slug}`, auth());
    await load();
    flash("Villa deleted");
  };

  const saveReview = async (review) => {
    try {
      const payload = { ...review };
      if (review._isNew) {
        delete payload._isNew;
        delete payload.id;
        await axios.post(`${API}/admin/reviews`, payload, auth());
      } else {
        await axios.put(`${API}/admin/reviews/${review.id}`, payload, auth());
      }
      await load();
      flash("Review saved");
    } catch (err) {
      setError(errText(err));
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await axios.delete(`${API}/admin/reviews/${id}`, auth());
    await load();
    flash("Review deleted");
  };

  const updateVilla = (i, patch) =>
    setVillas((vs) => vs.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const updateReview = (i, patch) =>
    setReviews((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory px-5" data-testid="admin-login-page">
        <form
          onSubmit={login}
          className="w-full max-w-sm border border-stone-900/10 bg-white p-10"
          data-testid="admin-login-form"
        >
          <p className="font-serif text-xl tracking-[0.22em] text-ink">
            BALIBIG<span className="text-gold">VILLAS</span>
          </p>
          <p className="mt-2 text-[11px] uppercase tracking-[0.25em] text-stone-500">
            Content Editor
          </p>
          <div className="relative mt-8">
            <input
              data-testid="admin-password-input"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className={`${inputCls} pr-10`}
            />
            <button
              type="button"
              data-testid="admin-password-toggle"
              onClick={() => setShowPw(!showPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400"
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-xs text-red-700">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            className="mt-6 w-full bg-ink py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-gold hover:text-ink"
          >
            Sign In
          </button>
        </form>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory" data-testid="admin-loading">
        <p className="text-sm text-stone-500">Loading content…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory pb-24" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-stone-900/10 bg-ivory/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5">
          <p className="font-serif text-lg tracking-[0.22em] text-ink">
            BALIBIG<span className="text-gold">VILLAS</span>
            <span className="ml-3 font-sans text-[10px] uppercase tracking-[0.25em] text-stone-500">
              Editor
            </span>
          </p>
          <div className="flex items-center gap-4">
            {notice && (
              <span data-testid="admin-notice" className="text-xs text-gold-dark">
                {notice}
              </span>
            )}
            <a href="/" className="text-[11px] uppercase tracking-[0.2em] text-stone-500 hover:text-ink" data-testid="admin-view-site">
              View Site
            </a>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-stone-500 hover:text-ink"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-5xl gap-1 px-5 pb-3">
          {["villas", "reviews", "settings"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`border px-5 py-2 text-[11px] font-medium uppercase tracking-[0.2em] transition-colors ${
                tab === t
                  ? "border-ink bg-ink text-ivory"
                  : "border-stone-900/15 text-stone-500 hover:border-gold"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 pt-10">
        {error && (
          <p data-testid="admin-error" className="mb-6 border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
            {error}
          </p>
        )}

        {tab === "settings" && (
          <div className="max-w-lg space-y-5 border border-stone-900/10 bg-white p-8" data-testid="admin-settings-panel">
            <h2 className="font-serif text-2xl font-light text-ink">Site Settings</h2>
            <Field
              label="WhatsApp number (international format, digits only)"
              data-testid="settings-whatsapp-number"
              value={config.whatsapp_number}
              onChange={(e) => setConfig({ ...config, whatsapp_number: e.target.value })}
            />
            <Field
              label="Default WhatsApp message"
              textarea={3}
              data-testid="settings-whatsapp-message"
              value={config.whatsapp_default_message}
              onChange={(e) =>
                setConfig({ ...config, whatsapp_default_message: e.target.value })
              }
            />
            <button
              data-testid="settings-save-button"
              onClick={saveConfig}
              className="flex items-center gap-2 bg-ink px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-gold hover:text-ink"
            >
              <Save size={14} /> Save Settings
            </button>
          </div>
        )}

        {tab === "villas" && (
          <div className="space-y-8" data-testid="admin-villas-panel">
            <button
              data-testid="villa-add-button"
              onClick={() => setVillas([...villas, { ...EMPTY_VILLA, _isNew: true, images: [""] }])}
              className="flex items-center gap-2 border border-ink px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-ivory"
            >
              <Plus size={14} /> Add Villa
            </button>
            {villas.map((v, i) => (
              <div
                key={v.slug || `new-${i}`}
                data-testid={`admin-villa-editor-${i}`}
                className="border border-stone-900/10 bg-white p-6 sm:p-8"
              >
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="font-serif text-xl font-light text-ink">
                    {v.title || "New Villa"}
                    {v._isNew && (
                      <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-gold-dark">
                        Unsaved
                      </span>
                    )}
                  </h3>
                  {!v._isNew && (
                    <button
                      data-testid={`villa-delete-${i}`}
                      onClick={() => deleteVilla(v.slug)}
                      className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-red-700 hover:text-red-900"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  )}
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field
                    label="Villa title"
                    data-testid={`villa-title-input-${i}`}
                    value={v.title}
                    onChange={(e) => updateVilla(i, { title: e.target.value })}
                  />
                  <label className="block">
                    <span className="mb-1.5 block text-[10px] font-medium uppercase tracking-[0.2em] text-stone-500">
                      Area
                    </span>
                    <select
                      data-testid={`villa-location-select-${i}`}
                      value={v.location.includes("Seminyak") ? "Seminyak" : "Canggu"}
                      onChange={(e) => updateVilla(i, { location: e.target.value })}
                      className={inputCls}
                    >
                      <option>Canggu</option>
                      <option>Seminyak</option>
                    </select>
                  </label>
                  <Field
                    label="Bedrooms"
                    type="number"
                    data-testid={`villa-bedrooms-input-${i}`}
                    value={v.bedrooms}
                    onChange={(e) => updateVilla(i, { bedrooms: Number(e.target.value) })}
                  />
                  <Field
                    label="Bathrooms"
                    type="number"
                    step="0.5"
                    data-testid={`villa-bathrooms-input-${i}`}
                    value={v.bathrooms ?? ""}
                    onChange={(e) => updateVilla(i, { bathrooms: Number(e.target.value) })}
                  />
                  <Field
                    label="Guests"
                    type="number"
                    data-testid={`villa-guests-input-${i}`}
                    value={v.guests}
                    onChange={(e) => updateVilla(i, { guests: Number(e.target.value) })}
                  />
                  <Field
                    label="Rating (e.g. 4.98)"
                    type="number"
                    step="0.01"
                    data-testid={`villa-rating-input-${i}`}
                    value={v.rating}
                    onChange={(e) => updateVilla(i, { rating: Number(e.target.value) })}
                  />
                  <Field
                    label="Reviews count"
                    type="number"
                    data-testid={`villa-reviews-count-input-${i}`}
                    value={v.reviews_count}
                    onChange={(e) =>
                      updateVilla(i, { reviews_count: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="mt-5 grid gap-5">
                  <Field
                    label="Airbnb listing URL"
                    data-testid={`villa-airbnb-input-${i}`}
                    value={v.airbnb_url}
                    onChange={(e) => updateVilla(i, { airbnb_url: e.target.value })}
                  />
                  <label className="flex items-center gap-3 text-sm text-stone-600">
                    <input
                      type="checkbox"
                      data-testid={`villa-airbnb-active-${i}`}
                      checked={!!v.airbnb_active}
                      onChange={(e) => updateVilla(i, { airbnb_active: e.target.checked })}
                      className="h-4 w-4 accent-[#C5A880]"
                    />
                    Airbnb link active (if off, image/title clicks fall back to WhatsApp)
                  </label>
                  <Field
                    label="WhatsApp prefilled message"
                    textarea={2}
                    data-testid={`villa-whatsapp-input-${i}`}
                    value={v.whatsapp_message}
                    onChange={(e) => updateVilla(i, { whatsapp_message: e.target.value })}
                  />
                  <Field
                    label="Short description"
                    textarea={3}
                    data-testid={`villa-description-input-${i}`}
                    value={v.description}
                    onChange={(e) => updateVilla(i, { description: e.target.value })}
                  />
                  <Field
                    label="Image URLs (one per line)"
                    textarea={4}
                    data-testid={`villa-images-input-${i}`}
                    value={(v.images || []).join("\n")}
                    onChange={(e) => updateVilla(i, { images: e.target.value.split("\n") })}
                  />
                </div>
                <button
                  data-testid={`villa-save-${i}`}
                  onClick={() => saveVilla(v)}
                  className="mt-6 flex items-center gap-2 bg-ink px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-gold hover:text-ink"
                >
                  <Save size={14} /> Save Villa
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "reviews" && (
          <div className="space-y-8" data-testid="admin-reviews-panel">
            <button
              data-testid="review-add-button"
              onClick={() => setReviews([...reviews, { ...EMPTY_REVIEW, _isNew: true }])}
              className="flex items-center gap-2 border border-ink px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-ivory"
            >
              <Plus size={14} /> Add Review
            </button>
            {reviews.map((r, i) => (
              <div
                key={r.id || `new-${i}`}
                data-testid={`admin-review-editor-${i}`}
                className="border border-stone-900/10 bg-white p-6 sm:p-8"
              >
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="font-serif text-xl font-light text-ink">
                    {r.author || "New Review"}
                    {r._isNew && (
                      <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-gold-dark">
                        Unsaved
                      </span>
                    )}
                  </h3>
                  {!r._isNew && (
                    <button
                      data-testid={`review-delete-${i}`}
                      onClick={() => deleteReview(r.id)}
                      className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-red-700 hover:text-red-900"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  )}
                </div>
                <div className="grid gap-5 sm:grid-cols-3">
                  <Field
                    label="Author"
                    data-testid={`review-author-input-${i}`}
                    value={r.author}
                    onChange={(e) => updateReview(i, { author: e.target.value })}
                  />
                  <Field
                    label="Country"
                    data-testid={`review-country-input-${i}`}
                    value={r.country}
                    onChange={(e) => updateReview(i, { country: e.target.value })}
                  />
                  <Field
                    label="Villa"
                    data-testid={`review-villa-input-${i}`}
                    value={r.villa}
                    onChange={(e) => updateReview(i, { villa: e.target.value })}
                  />
                </div>
                <div className="mt-5">
                  <Field
                    label="Quote"
                    textarea={3}
                    data-testid={`review-quote-input-${i}`}
                    value={r.quote}
                    onChange={(e) => updateReview(i, { quote: e.target.value })}
                  />
                </div>
                <button
                  data-testid={`review-save-${i}`}
                  onClick={() => saveReview(r)}
                  className="mt-6 flex items-center gap-2 bg-ink px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-gold hover:text-ink"
                >
                  <Save size={14} /> Save Review
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
